import SubmissionError from '../../../../error/SubmissionError'
import fetch from '../../../../utils/fetch'
import * as types from './mutation_types'

import { includes, has, split } from 'lodash'

export const reset = ({ commit }) => {
  commit(types.RESET)
}

export const retrieve = ({ commit }, id) => {
  commit(types.TOGGLE_LOADING)
  commit(types.SET_ERROR, '')

  return fetch(id)
    .then(response => response.json())
    .then((data) => {
      commit(types.SET_RETRIEVED, data)
    })
    .catch((e) => {
      commit(types.SET_ERROR, e.message)
    })
    .finally(() => {
      commit(types.TOGGLE_LOADING)
    })
}

export const update = ({ commit, state }) => {
  commit(types.TOGGLE_LOADING)
  commit(types.SET_ERROR, '')

  return fetch(state.retrieved['@id'], {
    method: 'PUT',
    headers: new Headers({ 'Content-Type': 'application/ld+json' }),
    body: JSON.stringify(state.retrieved)
  })
    .then(response => response.json())
    .then((data) => {
      commit(types.SET_UPDATED, data)
    })
    .catch((e) => {
      if (e instanceof SubmissionError) {
        commit(types.SET_VIOLATIONS, e.errors)
        // eslint-disable-next-line
        commit(types.SET_ERROR, e.errors._error)

        return
      }

      // eslint-disable-next-line
      commit(commit(types.SET_ERROR, e.errors._error))
    })
    .finally(() => {
      commit(types.TOGGLE_LOADING)
    })
}

export const updateRetrieved = ({ commit }, updated) => {
  commit(types.SET_RETRIEVED, updated)
}

export const getAllLanguages = ({ commit }, page = 'languages') => {
  commit(types.SET_LANGUAGES_TOGGLE_LOADING)
  commit(types.SET_ERROR, '')

  return fetch(page)
    .then(response => response.json())
    .then((data) => {
      commit(types.SET_LANGUAGES, data['hydra:member'])
    })
    .catch((e) => {
      commit(types.SET_ERROR, e.message)
    })
    .finally(() => {
      commit(types.SET_LANGUAGES_TOGGLE_LOADING)
    })
}

export const getTranslation = ({ commit, state }, languageCode) => {
  commit(types.SET_TRANSLATION_TOGGLE_LOADING)
  commit(types.SET_TRANSLATION_ERROR, '')

  return fetch(state.retrieved['@id'], {
    method: 'GET',
    headers: new Headers({ 'Accept-Language': languageCode })
  })
    .then(response => response.json())
    .then((data) => {
      commit(types.SET_TRANSLATION, data)
    })
    .catch((e) => {
      commit(types.SET_TRANSLATION_ERROR, e.message)
    })
    .finally(() => {
      commit(types.SET_TRANSLATION_TOGGLE_LOADING)
    })
}

export const saveTranslation = ({ commit, state }, languageCode) => {
  commit(types.SET_TRANSLATION_TOGGLE_LOADING)
  commit(types.SET_TRANSLATION_ERROR, '')

  return fetch(state.retrieved['@id'], {
    method: 'PUT',
    headers: new Headers({ 'Accept-Language': languageCode }),
    body: JSON.stringify({ description: state.translation.description })
  })
    .then(response => response.json())
    .catch((e) => {
      commit(types.SET_TRANSLATION_ERROR, e.message)
    })
    .finally(() => {
      commit(types.SET_TRANSLATION_TOGGLE_LOADING)
    })
}

export const updateTranslation = ({ commit }, updated) => {
  commit(types.SET_TRANSLATION, updated)
}

export const getAllBrands = ({ commit }, page = 'brands') => {
  commit(types.SET_BRANDS_TOGGLE_LOADING)
  commit(types.SET_ERROR, '')
  commit(types.SET_SELECTED_BRAND_TAB, null)

  return fetch(page)
    .then(response => response.json())
    .then((data) => {
      commit(types.SET_BRANDS, data['hydra:member'])
      if (data['hydra:member'].length > 0) {
        commit(types.SET_SELECTED_BRAND_TAB, data['hydra:member'][0])
      }
    })
    .catch((e) => {
      commit(types.SET_ERROR, e.message)
    })
    .finally(() => {
      commit(types.SET_BRANDS_TOGGLE_LOADING)
    })
}

export const setBrandTabSelected = ({ commit }, brand) => {
  commit(types.SET_SELECTED_BRAND_TAB, brand)
}

export const getBrandGameFileVersions = ({ commit }, { gameId, brandId, page, pageNumber }) => {
  commit(types.TOGGLE_GAME_FILE_VERSION_LOADING)
  commit(types.GAME_FILE_VERSION_ERROR, '')

  let pageUrl = `versions?gameFile.game.id=${gameId}&gameFile.brand.id=${brandId}&order[dateUploaded]=asc`
  if (page) {
    pageUrl = page
  } else if (pageNumber) {
    pageUrl += pageNumber
  }

  return fetch(pageUrl)
    .then(response => response.json())
    .then((data) => {
      commit(types.SET_GAME_FILE_VERSIONS, data['hydra:member'])
      commit(types.SET_GAME_FILE_VERSIONS_VIEW, data['hydra:view'])
      commit(types.SET_GAME_FILE_VERSIONS_TOTAL_ITEMS, data['hydra:totalItems'])
    })
    .catch((e) => {
      commit(types.GAME_FILE_VERSION_ERROR, e.message)
    })
    .finally(() => {
      commit(types.TOGGLE_GAME_FILE_VERSION_LOADING)
    })
}

export const uploadGameFileVersion = ({ commit, state, dispatch }, { game, brand, file }) => {
  commit(types.TOGGLE_GAME_FILE_VERSION_LOADING)
  commit(types.GAME_FILE_VERSION_ERROR, '')
  commit(types.GAME_FILE_VERSION_ERROR_SET_VIOLATIONS, null)

  const formData = new FormData()
  formData.append('file', file)
  formData.append('game', game)
  formData.append('brand', brand)

  return fetch('versions', {
    method: 'POST',
    body: formData
  })
    .then(response => response.json())
    .then((data) => {
      commit(types.UPLOADED_GAME_FILE_VERSION, data)

      if (redirectToNextPage(state, 'gameFileVersionsView', 'gameFileVersionsTotalItems')) {
        const gameId = split(game, '/api/games/')[1]
        const brandId = split(brand, '/api/brands/')[1]
        return dispatch('getBrandGameFileVersions', {
          gameId,
          brandId,
          pageNumber: redirectToNextPage(state, 'gameFileVersionsView', 'gameFileVersionsTotalItems')
        })
      } else {
        return new Promise(resolve => resolve())
      }
    })
    .catch((e) => {
      if (e instanceof SubmissionError) {
        commit(types.GAME_FILE_VERSION_ERROR_SET_VIOLATIONS, e.errors)
        // eslint-disable-next-line
        commit(types.GAME_FILE_VERSION_ERROR, e.errors._error)
        return
      }

      commit(types.GAME_FILE_VERSION_ERROR, e.message)
    })
    .finally(() => {
      commit(types.TOGGLE_GAME_FILE_VERSION_LOADING)
    })
}

export const removeGameFileVersion = ({ commit, state, dispatch }, { gameId, brandId, gameFileVersion }) => {
  commit(types.TOGGLE_GAME_FILE_VERSION_LOADING)
  commit(types.GAME_FILE_VERSION_ERROR, '')

  return fetch(gameFileVersion, {
    method: 'DELETE'
  })
    .then(() => {
      commit(types.DELETED_GAME_FILE_VERSION, gameFileVersion)

      if (redirectToPreviousPage(state, 'gameFileVersionsView', 'gameFileVersionsTotalItems')) {
        return dispatch('getBrandGameFileVersions', {
          gameId,
          brandId,
          pageNumber: redirectToPreviousPage(state, 'gameFileVersionsView', 'gameFileVersionsTotalItems')
        })
      } else {
        return new Promise(resolve => resolve())
      }
    })
    .catch((e) => {
      commit(types.GAME_FILE_VERSION_ERROR, e.message)
    })
    .finally(() => {
      commit(types.TOGGLE_GAME_FILE_VERSION_LOADING)
    })
}

function redirectToNextPage (state, view, totalItems) {
  const pageNumber = Math.ceil(state[totalItems] / 30)
  const keepSamePage = includes(state[view]['@id'], `page=${pageNumber}`)
  if (!has(state[view], 'hydra:first')) {
    if (state[totalItems] > 30) {
      return '&page=2'
    }
  } else if (!keepSamePage) {
    return `&page=${pageNumber}`
  }
  return null
}

function redirectToPreviousPage (state, view, totalItems) {
  if (has(state[view], 'hydra:first')) {
    const isAMultiple = Number.isInteger(state[totalItems] / 30)
    const currentPageNumber = split(state[view]['@id'], 'page=')[1]
    if (isAMultiple) {
      return `&page=${currentPageNumber - 1}`
    }
  }
  return null
}

export const publishGameFileVersion = ({ commit }, gameFileVersion) => {
  commit(types.PUBLISH_GAME_FILE_TOGGLE_LOADING)
  commit(types.PUBLISH_GAME_FILE_ERROR, '')
  commit(types.PUBLISHED_GAME_FILE, null)

  fetch(gameFileVersion, {
    method: 'PUT',
    body: JSON.stringify({ published: true })
  })
    .then(response => response.json())
    .then((data) => {
      commit(types.PUBLISHED_GAME_FILE, data)
    })
    .catch((e) => {
      commit(types.PUBLISH_GAME_FILE_ERROR, e.message)
    })
    .finally(() => {
      commit(types.PUBLISH_GAME_FILE_TOGGLE_LOADING)
    })
}
